import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";
import { ref, computed, inject, readonly } from "vue";
import { defineStore } from "pinia";

export const useEditAIPromptStore = defineStore("editAIPrompt", () => {
    const app = inject(PAGE_APP_SYMBOL);

    const editPromptModel = ref(null);
    const _requirementSuffixCounter = ref(0);
    const _projectDataSuffixCounter = ref(0);

    const projectDataIdList = ref([]);
    const requirementIdList = ref([]);

    const programId = computed(() => app.ProgramId);

    const promptModel = computed(() => editPromptModel.value?.PromptModel);
    const promptId = computed(() => promptModel.value?.PromptId);
    const promptSchema = computed(() => promptModel.value?.Schema || {});

    const projectDataById = computed(() => promptSchema.value?.ProjectData || {});
    const projectDataList = computed(() => Object.values(projectDataById.value));

    const requirementsById = computed(() => promptSchema.value?.Requirements || {});
    const requirementsList = computed(() => Object.values(requirementsById.value));

    // preview id - for running preview
    const previewPromptId = ref(null);

    // TEMP - for debugging state management
    const promptSchemaJson = computed(() => JSON.stringify(promptSchema.value, null, 2));

    const modelConfigurationType = computed(() => promptModel.value?.ModelType);

    // for preview
    const projectLoaded = ref(false);
    const projectDataFields = ref(projectDataList.value ?? []); // not reactive
    const projectDataTableLoading = ref(false);

    // generates the project data ID for the index (index starting at 1)
    function projectDataIdForIndex(index) {
        return `ProjectData_${index}`;
    }

    // generates the requirement ID for the index (index starting at 1)
    function requirementIdForIndex(index) {
        return `Requirement_${index}`;
    }

    // extract numeric suffix i from the project data id formatted as ProjectData_i
    function getSuffixFromProjectDataId(projectDataId) {
        const underscoreIndex = projectDataId.indexOf("_");
        return parseInt(projectDataId.substring(underscoreIndex + 1));
    }

    // extract numeric suffix i from the requirement id formatted as Requirement_i
    function getSuffixFromRequirementId(requirementId) {
        const underscoreIndex = requirementId.indexOf("_");
        return parseInt(requirementId.substring(underscoreIndex + 1));
    }

    function loadState(model) {
        editPromptModel.value = model;
        _requirementSuffixCounter.value = requirementsList.value.length + 1;
        _projectDataSuffixCounter.value = projectDataList.value.length + 1;

        // retrieve and sort by suffix list of project data ids and list of requirement ids
        projectDataIdList.value = Object.keys(projectDataById.value).sort(function (a, b) {
            return getSuffixFromProjectDataId(a) - getSuffixFromProjectDataId(b);
        });
        requirementIdList.value = Object.keys(requirementsById.value).sort(function (a, b) {
            return getSuffixFromRequirementId(a) - getSuffixFromRequirementId(b);
        });
    }

    function _getProjectDataById(projectDataId) {
        return projectDataById.value[projectDataId];
    }
    function addProjectData() {
        const idSuffix = _projectDataSuffixCounter.value++;
        const projectData = {
            PublicId: projectDataIdForIndex(idSuffix),
            Label: "New Label",
            TemplateTagText: ""
        };
        promptSchema.value.ProjectData[projectData.PublicId] = projectData;

        projectDataIdList.value.push(projectData.PublicId);
        app.setUnsavedChanges(true);
    }
    function editProjectData(projectDataId, changes) {
        const projectData = _getProjectDataById(projectDataId);
        promptSchema.value.ProjectData[projectDataId] = { ...projectData, ...changes };
        app.setUnsavedChanges(true);
    }
    function deleteProjectData(projectDataId) {
        const updatedProjectData = {}; // create new project data object

        const indexToDelete = projectDataIdList.value.indexOf(projectDataId);

        // add all the project data entries that precede the deleted entry to the new project data object
        for (let j = 0; j < indexToDelete; j++) {
            updatedProjectData[projectDataIdList.value[j]] = _getProjectDataById(projectDataIdList.value[j]);
        }

        // for all project data after the deleted entry, update the key/publicId to reflect new index in the project data
        for (let i = indexToDelete + 1; i < projectDataIdList.value.length; i++) {
            const newData = _getProjectDataById(projectDataIdList.value[i]);

            // shifting the public id/key down by 1
            newData["PublicId"] = projectDataIdForIndex(i);
            updatedProjectData[newData["PublicId"]] = newData;
        }

        // update project data to new project data object
        promptSchema.value.ProjectData = updatedProjectData;

        // decrement suffix counter to reflect new project data count
        _projectDataSuffixCounter.value--;

        // remove last entry from projectDataIdList, since all entries have been shifted down 1
        // and the last id is no longer in use
        projectDataIdList.value.pop();

        app.setUnsavedChanges(true);
    }

    function _getRequirementById(requirementId) {
        return requirementsById.value[requirementId];
    }
    function addRequirement() {
        const idSuffix = _requirementSuffixCounter.value++;
        const req = {
            PublicId: requirementIdForIndex(idSuffix),
            Text: ""
        };
        promptSchema.value.Requirements[req.PublicId] = req;

        requirementIdList.value.push(req.PublicId);

        app.setUnsavedChanges(true);
    }
    function editRequirement(requirementId, changes) {
        const req = _getRequirementById(requirementId);
        promptSchema.value.Requirements[requirementId] = { ...req, ...changes };

        app.setUnsavedChanges(true);
    }
    function deleteRequirement(requirementId) {
        const updatedRequirements = {}; // create new requirements object
        const indexToDelete = requirementIdList.value.indexOf(requirementId);

        // add all the requirement entries that precede the deleted entry to the new requirement object
        for (let j = 0; j < indexToDelete; j++) {
            updatedRequirements[requirementIdList.value[j]] = _getRequirementById(requirementIdList.value[j]);
        }

        // for all requirement after the deleted entry, update the key/publicId to reflect new index in the requirement
        for (let i = indexToDelete + 1; i < requirementIdList.value.length; i++) {
            const newRequirement = _getRequirementById(requirementIdList.value[i]);

            // shifting the public id/key down by 1
            newRequirement["PublicId"] = requirementIdForIndex(i);
            updatedRequirements[newRequirement["PublicId"]] = newRequirement;
        }

        // update requirement to new requirement object
        promptSchema.value.Requirements = updatedRequirements;

        // decrement suffix counter to reflect new requirement count
        _requirementSuffixCounter.value--;

        // remove last entry from requirementIdList, since all entries have been shifted down 1
        // and the last id is no longer in use
        requirementIdList.value.pop();

        app.setUnsavedChanges(true);
    }

    const displayName = computed({
        get: () => promptModel.value?.DisplayName,
        set: (newValue) => {
            if (promptModel.value) {
                promptModel.value.DisplayName = newValue;
                app.setUnsavedChanges(true);
            }
        }
    });

    const description = computed({
        get: () => promptModel.value?.Description,
        set: (newValue) => {
            if (promptModel.value) {
                promptModel.value.Description = newValue;
            }
        }
    });

    const systemInstructions = computed({
        get: () => promptSchema.value?.SystemInstructions,
        set: (newValue) => {
            if (promptSchema.value) {
                promptSchema.value.SystemInstructions = newValue;
                app.setUnsavedChanges(true);
            }
        }
    });

    const modelTemperature = computed({
        get: () => promptSchema.value?.Parameters.Temperature,
        set: (newValue) => {
            if (promptSchema.value && promptSchema.value.Parameters) {
                promptSchema.value.Parameters.Temperature = newValue;
                app.setUnsavedChanges(true);
            }
        }
    });

    const modelTopP = computed({
        get: () => promptSchema.value?.Parameters.TopP,
        set: (newValue) => {
            if (promptSchema.value && promptSchema.value.Parameters) {
                promptSchema.value.Parameters.TopP = newValue;
                app.setUnsavedChanges(true);
            }
        }
    });

    const modelMaxTokens = computed({
        get: () => promptSchema.value?.Parameters.MaxTokens,
        set: (newValue) => {
            if (promptSchema.value && promptSchema.value.Parameters) {
                promptSchema.value.Parameters.MaxTokens = newValue;
                app.setUnsavedChanges(true);
            }
        }
    });

    const isPromptDraft = computed({
        get: () => promptModel.value?.IsDraft,
        set: (newValue) => {
            if (promptModel.value) {
                promptModel.value.IsDraft = newValue;
            }
        }
    });

    return {
        promptId: readonly(promptId),
        programId: readonly(programId),
        promptModel: readonly(promptModel),
        projectDataList: readonly(projectDataList),
        projectDataById: readonly(projectDataById),
        requirementsList: readonly(requirementsList),
        requirementsById: readonly(requirementsById),
        promptSchemaJson: readonly(promptSchemaJson),
        displayName,
        description,
        systemInstructions,
        modelConfigurationType,
        modelTemperature,
        modelTopP,
        modelMaxTokens,
        projectLoaded,
        projectDataFields,
        projectDataTableLoading,
        isPromptDraft,
        previewPromptId,
        loadState,
        addProjectData,
        editProjectData,
        deleteProjectData,
        addRequirement,
        editRequirement,
        deleteRequirement
    };
});
